import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  drawerStyle,
  headerMessage,
  stickyHeader,
  tableRowSize,
  defaultAllSelection,
} from "services/constants";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { payrollGroupHeader } from "pages/PayRoll/PayRollHeaders";
import { Box, Paper, Drawer } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { CustomButton } from "components/CustomComponents";
import { showToast } from "services/utils/Status";
import { CreatePayrollGroup } from "./CreatePayrollGroup";
import { ViewPayrollGroup } from "./ViewPayrollGroup";
import moment from "moment";
import { useSaveView } from "components/CustomComponents/useSaveView";

export const PayrollGroup = ({ subModuleId }) => {
  const {
    domain,
    userScreenAccess,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
  } = useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [payrollGroupDetails, setPayrollGroupDetails] = useState([]);
  const containerRef = useRef(null);
  const [individualPayrollGrpData, setIndividualPayrollGrpData] = useState([]);
  // See PayRoll/Configuration/PayrollConfiguration/index.js — neither "Configuration" nor "Setup" ever
  // matched any real backend module_name; the correct one is "Settings".
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Settings",
    "Payroll Group"
  );

  const {
    result: payrollGrpData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}payroll/setup/payrollgroup/payroll-group/?company=${
      parseInt(topbarCompanyId) === defaultAllSelection ? "" : topbarCompanyId
    }`,
  );
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [hanldeLoader, setHanldeLoader] = useState(false);
  const [getId, setGetId] = useState("");

  const hanldeCreatePanel = () => {
    setIsCreatePanelOpen(true);
  };

  const handleViewPanel = () => {
    setIsViewPanelOpen(true);
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (payrollGrpData?.length !== 0 && payrollGrpData && hanldeLoader) {
      const item = payrollGrpData.find((d) => d.id === getId);
      setIndividualPayrollGrpData(item);
    }
  }, [getId, hanldeLoader, payrollGrpData]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setPayrollGroupDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const NewPayrunButton = (
    <CustomButton
      actionFuntion={() => {
        if (check_access) {
          hanldeCreatePanel();
        } else {
          showToast(
            "error",
            "Access denied: You are not authorized to create payroll group"
          );
        }
      }}
      actionName="New"
      typeName="button"
    />
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setHanldeLoader(false);
    }
  }, [reCallApi, topbarCompanyId]);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "payroll_group_saved_views",
    fullHeaders: payrollGroupHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  return !hanldeLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        {headerMessage(
          "Payroll Group",
          "Manage employee groups for efficient and accurate payroll runs.",
        )}
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Payroll Group Records"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={payrollGrpData}
            filteredData={payrollGroupDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={
              <>
                {newButtonComponent}
                {NewPayrunButton}
              </>
            }
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {visibleHeaders?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {payrollGroupDetails?.length === 0 ? (
                    <TableEmptyState dataLength={payrollGrpData} />
                  ) : (
                    payrollGroupDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell
                              onClick={() => {
                                handleViewPanel();
                                setIndividualPayrollGrpData(value);
                              }}
                            >
                              {value[head.key] ?? "N/A"}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {payrollGroupDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={payrollGroupDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen}
        onClose={() => {
          isCreatePanelOpen ? handleCreateClosePanel() : handleViewClosePanel();
        }}
        sx={drawerStyle}
      >
        {isCreatePanelOpen ? (
          <CreatePayrollGroup
            onClose={handleCreateClosePanel}
            reCallApi={reCallApi}
            setHanldeLoader={setHanldeLoader}
          />
        ) : isViewPanelOpen ? (
          <ViewPayrollGroup
            onClose={handleViewClosePanel}
            individualPayrollGrpData={individualPayrollGrpData}
            reCallApi={reCallApi}
            setGetId={setGetId}
            setHanldeLoader={setHanldeLoader}
            screenAccess={check_access}
          />
        ) : null}
      </Drawer>
    </React.Fragment>
  );
};
